import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../AllProducts/AllProducts.css";
import "../Metal/MetalDetail.css";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import BoardsDetailExploreSlider from "../../components/pages/AllProducts/Boards/BoardsDetailExploreSlider/BoardsDetailExploreSlider";
import AllSolutionsRequestNewProject from "../../components/pages/AllSolutions/AllSolutionsRequestNewProject/AllSolutionsRequestNewProject";
import { getMetalFrameById, getMetalFrames } from "../../api/metalFrames";
import { resolveMediaUrl } from "../../utils/media";
import { filterPdfRowsByProductSlug, mapPublicProductPdfs, pickPublicPdfArray } from "../../utils/productPdfs";
import {
  buildDimensionColumnKeys,
  getDimensionValue,
  normalizeKey,
  sectionDefaultsFromLabelValuePairs,
  toFlexibleDimensionsTableRows,
  toHeaderLabel,
} from "../../utils/metalFrameDimensionsTable";
import {
  expandMaterialPropertyRows,
  stressDefaultsForSectionDimensions,
} from "../../utils/yieldUltimateStressMaterial";
import SolutionDetailsDownloads from "../../components/pages/SolutionDetails/SolutionDetailsDownloads/SolutionDetailsDownloads";

const TAB_ITEMS = [
  { id: "standards", label: "Standards" },
  { id: "specifications", label: "Specifications" },
  { id: "dimensions", label: "Dimensions" },
  { id: "downloads", label: "Downloads" },
];

const CeilingMetalFrameDetail = () => {
  const { frameId } = useParams();
  const { i18n } = useTranslation();
  const [apiItem, setApiItem] = useState(null);
  const [listMatch, setListMatch] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [activeTab, setActiveTab] = useState("standards");
  const sectionRefs = useRef({
    standards: null,
    specifications: null,
    dimensions: null,
    downloads: null,
  });

  useEffect(() => {
    let isMounted = true;
    setResolved(false);

    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      let matched = null;

      try {
        const list = await getMetalFrames({ language, frameType: "ceiling-metal-frame" });
        const normalized = Array.isArray(list) ? list : [];
        matched =
          normalized.find((item) => String(item?.frameKey || "") === String(frameId)) ||
          normalized.find((item) => String(item?.id || "") === String(frameId)) ||
          null;
        if (isMounted) {
          setListMatch(matched || null);
        }
      } catch {
        // fall through to direct id
      }

      if (!matched) {
        try {
          const byId = await getMetalFrameById({ id: frameId, language });
          if (byId && String(byId?.frameType || "").toLowerCase() === "ceiling-metal-frame") {
            matched = byId;
          }
        } catch {
          // keep null
        }
      }

      if (isMounted) {
        setApiItem(matched);
        if (!matched) {
          setListMatch(null);
        }
        setResolved(true);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [frameId, i18n.language]);

  const item = useMemo(() => {
    if (!apiItem) return null;
    const toTextRows = (list) =>
      Array.isArray(list)
        ? list.map((x) => (typeof x === "string" ? x : x?.text || "")).filter(Boolean)
        : [];
    const toPairRows = (list) =>
      Array.isArray(list)
        ? list.map((x) => ({ label: x?.label || "", value: x?.value || "" })).filter((x) => x.label || x.value)
        : [];
    const framePdfHints = [frameId, apiItem?.frameKey, listMatch?.frameKey];
    const mergedFramePdfs = (...sources) =>
      mapPublicProductPdfs(filterPdfRowsByProductSlug(pickPublicPdfArray(...sources), ...framePdfHints));

    const matPairs = toPairRows(apiItem?.materialProperties);

    // Strip any "Yield stress, Fy" / "Ultimate stress, Fu" entries from section defaults so
    // they cannot leak in as dimensions columns. Matches the accessory + drywall filter.
    const stripStressKeys = (obj) =>
      Object.fromEntries(
        Object.entries(obj || {}).filter(([key]) => {
          const text = String(key || "").toLowerCase();
          return !/yield\s*stress/.test(text) && !/ultimate\s*stress/.test(text);
        })
      );

    return {
      heroTag: apiItem?.heroTag || "METAL FRAMES",
      heroTitle: apiItem?.heroTitle || apiItem?.name || "Ceiling Metal Frame",
      heroDescription: apiItem?.heroDescription || "",
      heroDescriptionMore: apiItem?.heroDescriptionMore || "",
      heroBullets: toTextRows(apiItem?.heroBullets),
      heroImage: resolveMediaUrl(apiItem?.imageUrl) || "",
      standards: toTextRows(apiItem?.standards),
      materialProperties: expandMaterialPropertyRows(matPairs),
      sectionDimensionDefaults: stripStressKeys({
        ...sectionDefaultsFromLabelValuePairs(apiItem?.sectionDimensions),
        ...stressDefaultsForSectionDimensions(matPairs),
      }),
      dimensionsTable: toFlexibleDimensionsTableRows(apiItem?.dimensionsTable),
      isCrossSection: Boolean(apiItem?.isCrossSection ?? listMatch?.isCrossSection),
      diagramUrl: apiItem?.diagramUrl ?? listMatch?.diagramUrl ?? "",
      pdfs: mergedFramePdfs(apiItem, listMatch),
    };
  }, [apiItem, frameId, listMatch]);

  const isRapidHangerFix = useMemo(() => {
    if (!item) return false;
    const byTitle = /rapid\s*hanger\s*fix/i.test(String(item.heroTitle || ""));
    const byId = /rapid-hanger-fix/i.test(String(frameId || ""));
    return byTitle || byId;
  }, [item, frameId]);

  const isThreadedRod = useMemo(() => {
    if (!item) return false;
    // Matches both "Threaded Rod" and the in-product spelling "Threated Rod".
    const byTitle = /threa[td]ed\s*rod/i.test(String(item.heroTitle || ""));
    const byId = /threa[td]ed[-_]?rod/i.test(String(frameId || ""));
    return byTitle || byId;
  }, [item, frameId]);

  const matchesProductIdentifier = (regex) => {
    const haystacks = [
      item?.heroTitle,
      apiItem?.heroTitle,
      apiItem?.name,
      listMatch?.heroTitle,
      listMatch?.name,
      frameId,
    ];
    return haystacks.some((value) => regex.test(String(value || "")));
  };

  const isCd60Channel = useMemo(() => {
    if (!item) return false;
    // Matches "CD60", "CD 60", "CD-60", "CD60 Channel", "cd-60-channel", etc.
    return matchesProductIdentifier(/\bcd[-_\s]*60(?:[-_\s]*channel)?\b/i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, apiItem, listMatch, frameId]);

  const isUdChannel = useMemo(() => {
    if (!item) return false;
    // Matches "UD Channel" and "UD28 Channel" (or "UD-28-Channel", etc.) — any
    // UD channel product, with or without a digit suffix.
    return matchesProductIdentifier(/\bud[-_\s]*\d*[-_\s]*channel\b/i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, apiItem, listMatch, frameId]);

  /** Field aliases for the Threaded Rod table (dashboard uses generic keys, API may also expose custom keys). */
  const THREADED_ROD_COLUMNS = useMemo(
    () => [
      {
        key: "productcode",
        label: "Product Code",
        lookups: ["productCode", "code", "role"],
      },
      {
        key: "om",
        label: "ØM",
        lookups: ["oM", "om", "diameter", "thread", "h1"],
      },
      {
        key: "effectivesection",
        label: "Effective section (As)",
        lookups: ["effectiveSection", "as", "area", "width"],
      },
      {
        key: "steelgrade48",
        label: (
          <>
            Steel Grade 4.8 /
            <br />
            Characteristic tensile strength Nk,s [kN]
          </>
        ),
        lookups: ["steelGrade48", "steelGrade4_8", "grade48", "tensile48", "thickness"],
      },
      {
        key: "steelgrade88",
        label: (
          <>
            Steel Grade 8.8 /
            <br />
            Characteristic tensile strength Nk,s [kN]
          </>
        ),
        lookups: ["steelGrade88", "steelGrade8_8", "grade88", "tensile88", "length"],
      },
    ],
    []
  );

  const visibleTabs = useMemo(() => {
    if (isRapidHangerFix) {
      return TAB_ITEMS.filter((tab) => tab.id !== "dimensions");
    }
    return TAB_ITEMS;
  }, [isRapidHangerFix]);

  useEffect(() => {
    setActiveTab("standards");
  }, [frameId]);

  useEffect(() => {
    if (isRapidHangerFix && activeTab === "dimensions") {
      setActiveTab("standards");
    }
  }, [isRapidHangerFix, activeTab]);

  const heroText = useMemo(() => {
    if (!item) return "";
    return `${item.heroDescription || ""}${item.heroDescriptionMore || ""}`.trim();
  }, [item]);

  const dimensionColumns = useMemo(() => {
    if (isThreadedRod) {
      return THREADED_ROD_COLUMNS.map(({ key, label }) => ({ key, label }));
    }
    const HIDDEN_ON_CEILING = new Set(["yieldstressfy", "ultimatestressfu"]);
    const DEFAULT_ORDER = [
      "productcode",
      "width",
      "height",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const H1_H2_ORDER = [
      "productcode",
      "h1",
      "width",
      "h2",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const H1_H2_W1_W2_ORDER = [
      "productcode",
      "h1",
      "w1",
      "h2",
      "w2",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const SLOT_ORDER = [
      "productcode",
      "width",
      "height",
      "slotwidth",
      "slotspacing",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const SHOULDER_WEB_H1_H2_ORDER = [
      "productcode",
      "width",
      "webwidth",
      "h1",
      "h2",
      "shoulder",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const LIP_FACING_ORDER = [
      "productcode",
      "height",
      "width",
      "lip",
      "facingwidth",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const FLANGE_ORDER = [
      "productcode",
      "flange",
      "width",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];

    const baseKeys = buildDimensionColumnKeys(
      item?.dimensionsTable,
      item?.sectionDimensionDefaults
    ).filter((key) => !HIDDEN_ON_CEILING.has(normalizeKey(key)));

    const normalizedSet = new Set(baseKeys.map((k) => normalizeKey(k)));
    const hasH1 = normalizedSet.has("h1");
    const hasH2 = normalizedSet.has("h2");
    const hasW1 = normalizedSet.has("w1");
    const hasW2 = normalizedSet.has("w2");
    const hasH1H2W1W2 = hasH1 && hasH2 && hasW1 && hasW2;
    const hasSlots =
      normalizedSet.has("slotwidth") && normalizedSet.has("slotspacing");
    const hasShoulderWeb =
      normalizedSet.has("shoulder") &&
      normalizedSet.has("webwidth") &&
      hasH1 &&
      hasH2;
    const hasLipFacing =
      normalizedSet.has("lip") && normalizedSet.has("facingwidth");
    // Match "flange", "flanges", "flangeWidth", etc. — any column whose
    // normalized name starts with "flange".
    const hasFlange = Array.from(normalizedSet).some((k) => k.startsWith("flange"));

    // Hide the generic "Height" column when H1 is present (covered by H1/H2
    // columns), when Flange is present (Height doesn't apply to flange profiles),
    // or specifically on the CD60 / UD Channel products.
    const shouldHideHeight = hasH1 || hasFlange || isCd60Channel || isUdChannel;
    // CD60 and UD Channel profiles don't expose H1 either — hide it.
    const shouldHideH1 = isCd60Channel || isUdChannel;
    // When H1/H2 + W1/W2 are present, hide the generic "Width" column — width
    // info comes from W1/W2.
    const shouldHideWidth = hasH1H2W1W2;
    const keys = baseKeys.filter((k) => {
      const normalized = normalizeKey(k);
      if (shouldHideHeight && normalized === "height") return false;
      if (shouldHideH1 && normalized === "h1") return false;
      if (shouldHideWidth && normalized === "width") return false;
      return true;
    });

    const activeOrder = hasH1H2W1W2
      ? H1_H2_W1_W2_ORDER
      : hasShoulderWeb
      ? SHOULDER_WEB_H1_H2_ORDER
      : hasH1 && hasH2
      ? H1_H2_ORDER
      : hasLipFacing
      ? LIP_FACING_ORDER
      : hasFlange
      ? FLANGE_ORDER
      : hasSlots
      ? SLOT_ORDER
      : DEFAULT_ORDER;
    const orderIndex = (normalized) => {
      const idx = activeOrder.indexOf(normalized);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const sortedKeys = keys
      .map((key, index) => ({ key, index, normalized: normalizeKey(key) }))
      .sort((a, b) => {
        // When only H1 exists (no H2), treat H1 as the "height" slot for ordering.
        const aOrderKey = hasH1 && !hasH2 && a.normalized === "h1" ? "height" : a.normalized;
        const bOrderKey = hasH1 && !hasH2 && b.normalized === "h1" ? "height" : b.normalized;
        const diff = orderIndex(aOrderKey) - orderIndex(bOrderKey);
        return diff !== 0 ? diff : a.index - b.index;
      })
      .map(({ key }) => key);

    return sortedKeys.map((key) => {
      const normalized = normalizeKey(key);
      const label =
        hasH1 && !hasH2 && normalized === "h1" ? "Height" : toHeaderLabel(key);
      return { key, label };
    });
  }, [item, isThreadedRod, isCd60Channel, isUdChannel, THREADED_ROD_COLUMNS]);

  const formatDimensionCell = (raw) => {
    if (raw == null) return "";
    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
      return String(raw);
    }
    if (Array.isArray(raw)) {
      return raw.map((v) => formatDimensionCell(v)).filter(Boolean).join(", ");
    }
    if (typeof raw === "object") {
      const language = String(i18n.language || "en").toLowerCase();
      const known =
        raw[language] ??
        raw.value ??
        raw.text ??
        raw.label ??
        raw.amount ??
        raw.display ??
        raw.en ??
        raw.name ??
        null;
      if (typeof known === "string" || typeof known === "number") return String(known);
      // Last resort: pick the first primitive own value (skips nested objects).
      for (const v of Object.values(raw)) {
        if (typeof v === "string" || typeof v === "number") return String(v);
      }
      return "";
    }
    return "";
  };

  const isStressRoleRow = (row) => {
    const role = String(row?.role || "").toLowerCase();
    if (!role) return false;
    return /yield\s*stress/.test(role) || /ultimate\s*stress/.test(role);
  };

  const getThreadedRodCellValue = (row, key) => {
    const col = THREADED_ROD_COLUMNS.find((c) => c.key === key);
    if (!col) return "";
    for (const lookup of col.lookups) {
      const value = formatDimensionCell(row?.[lookup]);
      if (value.trim()) return value;
    }
    return "";
  };

  const visibleDimensionRows = useMemo(() => {
    const rows = Array.isArray(item?.dimensionsTable) ? item.dimensionsTable : [];
    const defaults = item?.sectionDimensionDefaults || {};
    if (isThreadedRod) {
      return rows.filter((row) =>
        dimensionColumns.some(({ key }) => getThreadedRodCellValue(row, key).trim() !== "")
      );
    }
    return rows
      .filter((row) => !isStressRoleRow(row))
      .filter((row) =>
        dimensionColumns.some(
          ({ key }) => formatDimensionCell(getDimensionValue(row, key, defaults)).trim() !== ""
        )
      );
  }, [item, dimensionColumns, isThreadedRod]);

  /** Zinc coating is a frame-wide spec (e.g. "G40-G90") — propagate the first non-empty row value to all rows. */
  const sharedZincCoating = useMemo(() => {
    const rows = Array.isArray(item?.dimensionsTable) ? item.dimensionsTable : [];
    const defaults = item?.sectionDimensionDefaults || {};
    for (const row of rows) {
      const value = formatDimensionCell(
        getDimensionValue(row, "zincCoating", defaults)
      ).trim();
      if (value) return value;
    }
    return "";
  }, [item]);

  const getCellValue = (row, key) => {
    if (isThreadedRod) {
      return getThreadedRodCellValue(row, key);
    }
    const formatted = formatDimensionCell(
      getDimensionValue(row, key, item?.sectionDimensionDefaults || {})
    );
    if (!formatted.trim() && normalizeKey(key) === "zinccoating" && sharedZincCoating) {
      return sharedZincCoating;
    }
    return formatted;
  };

  if (!resolved) return null;
  if (!item) return <Navigate to="/products/ceiling-metal-frame" replace />;

  const frameSpecDiagramSrc = item.isCrossSection ? resolveMediaUrl(item.diagramUrl || "") : "";
  const frameSpecsGridUsesTwoColumns = !item.isCrossSection || Boolean(frameSpecDiagramSrc);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const targetSection = sectionRefs.current[tabId];
    if (!targetSection) return;
    const topOffset = 100;
    const sectionTop = targetSection.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: sectionTop - topOffset,
      behavior: "smooth",
    });
  };

  return (
    <div className="products-page metalDetailPage">
      <div className="products-page-content-guide">
        <Navbar theme="dark" />

        <section className="metalDetailHero" aria-label={item.heroTitle}>
          <div className="metalDetailHero-inner">
            <div>
              <span className="metalDetailHero-tag">{item.heroTag}</span>
              <h1 className="metalDetailHero-title">{item.heroTitle}</h1>
              <p className="metalDetailHero-desc">{heroText}</p>
            </div>
            <div className="metalDetailHero-imageWrap">
              <img src={item.heroImage} alt="" className="metalDetailHero-image" draggable={false} />
            </div>
          </div>
        </section>

        <div className="metalDetailTabsWrap">
          <nav className="metalDetailTabs" aria-label="Ceiling metal frame detail sections">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`metalDetailTab ${activeTab === tab.id ? "is-active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <section
          className="metalDetailSection"
          ref={(node) => {
            sectionRefs.current.standards = node;
          }}
        >
          <div className="metalDetailSection-inner">
            <h2 className="metalDetailHeading">Standards</h2>
            <div className="metalStandardsGrid">
              {item.standards.map((text, index) => (
                <div className="metalStandardsCard" key={`${index}-${text.slice(0, 12)}`}>
                  <span className="metalStandardsIndex">{String(index + 1).padStart(2, "0")}</span>
                  <p className="metalStandardsText">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="metalSpecsSection"
          ref={(node) => {
            sectionRefs.current.specifications = node;
          }}
        >
          <div className="metalDetailSection-inner">
            <h2 className="metalDetailHeading">Specifications</h2>
            <div className={`metalSpecsGrid${frameSpecsGridUsesTwoColumns ? "" : " metalSpecsGrid--single"}`}>
              <div>
                <h3 className="metalSpecsBlockTitle">Material Properties</h3>
                <div className="metalSpecsRule" />
                <dl className="metalSpecsRows">
                  {item.materialProperties.map((row, idx) => (
                    <div className="metalSpecsRow" key={`${row.label}-${idx}`}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {item.isCrossSection && frameSpecDiagramSrc ? (
                <div className="metalSpecsDiagramCol">
                  <h3 className="metalSpecsBlockTitle">Diagram</h3>
                  <div className="metalSpecsRule" />
                  <div className="metalSpecsDiagramWrap">
                    <img
                      src={frameSpecDiagramSrc}
                      alt={`${item.heroTitle || "Metal frame"} cross-section diagram`}
                      className="metalSpecsDiagramImg"
                      draggable={false}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {!isRapidHangerFix && (
          <section
            className="metalDimensionsSection"
            ref={(node) => {
              sectionRefs.current.dimensions = node;
            }}
          >
            <div className="metalDetailSection-inner">
              <h2 className="metalDetailHeading">Dimensions</h2>
              <div className="metalDimensionsTableWrap">
                <table className="metalDimensionsTable">
                  <thead>
                    <tr>
                      {dimensionColumns.map(({ key, label }) => (
                        <th key={`col-${key}`}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDimensionRows.map((row, index) => (
                      <tr key={`${row.role}-${index}`}>
                        {dimensionColumns.map(({ key }) => (
                          <td key={`cell-${key}-${index}`}>
                            {getCellValue(row, key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        <section
          ref={(node) => {
            sectionRefs.current.downloads = node;
          }}
        >
          <SolutionDetailsDownloads
            items={(item.pdfs || []).map((p) => ({
              id: p.id,
              title: p.title,
              href: p.href,
            }))}
            downloadsDescription="Technical PDFs and documents for this metal frame."
          />
        </section>

        <BoardsDetailExploreSlider />
        <AllSolutionsRequestNewProject />
      </div>
      <Footer />
    </div>
  );
};

export default CeilingMetalFrameDetail;
